import { t } from '../services/language-manager';
import { isConversationInfo, needsWebhook, usesCatalog } from '../services/action-types';
import type { CatalogSummary } from '../services/wizard-backend';

export interface ReadinessItem {
  key: 'webhook' | 'catalog' | 'fields';
  done: boolean;
}

export function catalogLoaded(summary: CatalogSummary | null): boolean {
  return (summary?.products ?? 0) > 0;
}

/**
 * What must be done before an action can be switched on: for a webhook
 * action a successful webhook test and, for catalog types, a loaded catalog
 * (both need the saved action: the test and the import take its actionId);
 * for Conversation info at least one saved field. Empty for AI Reply.
 */
export function enableRequirements(
  type: string,
  state: { webhookTested: boolean; catalogLoaded: boolean; hasInfoFields?: boolean },
): ReadinessItem[] {
  if (isConversationInfo(type)) return [{ key: 'fields', done: state.hasInfoFields === true }];
  if (!needsWebhook(type)) return [];
  const items: ReadinessItem[] = [
    { key: 'webhook', done: state.webhookTested },
  ];
  if (usesCatalog(type)) {
    items.push({ key: 'catalog', done: state.catalogLoaded });
  }
  return items;
}

/**
 * The saved card's instruction while it cannot be switched on: one plain
 * sentence naming what is still outstanding (no marks; nothing here is a control).
 * `unsavedTestPassed`: the form's unsaved webhook settings passed a test, so
 * saving them is what remains for the webhook. Null once everything is done.
 */
export function readinessInstruction(items: ReadinessItem[], unsavedTestPassed = false): string | null {
  if (items.some((r) => r.key === 'fields' && !r.done)) return t('settings-actions-ready-need-fields');
  const webhook = items.some((r) => r.key === 'webhook' && !r.done);
  const catalog = items.some((r) => r.key === 'catalog' && !r.done);
  if (webhook && unsavedTestPassed) {
    return t(catalog ? 'settings-actions-ready-need-save-catalog' : 'settings-actions-ready-need-save');
  }
  if (webhook && catalog) return t('settings-actions-ready-need-both');
  if (webhook) return t('settings-actions-ready-need-webhook');
  if (catalog) return t('settings-actions-ready-need-catalog');
  return null;
}
